import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Download, CheckCircle, XCircle, Clock, DollarSign, Eye, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const STATUS_CONFIG = {
  submitted: { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  under_review: { label: 'Under Review', color: 'bg-blue-100 text-blue-800', icon: Eye },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
  paid: { label: 'Paid', color: 'bg-emerald-100 text-emerald-800', icon: DollarSign },
};

const VendorInvoiceReview = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [acting, setActing] = useState(false);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await api.get(`/hoa/vendor-invoices${params}`);
      setInvoices(res.data.invoices || []);
    } catch (err) {
      console.error('Error fetching invoices:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const counts = {
    all: invoices.length,
    submitted: invoices.filter(i => i.status === 'submitted').length,
    under_review: invoices.filter(i => i.status === 'under_review').length,
    approved: invoices.filter(i => i.status === 'approved').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    rejected: invoices.filter(i => i.status === 'rejected').length,
  };

  const handleReview = async (invoiceId, action, role = 'review') => {
    setActing(true);
    try {
      const endpoint = role === 'board'
        ? `/hoa/vendor-invoices/${invoiceId}/board-approve`
        : role === 'paid'
        ? `/hoa/vendor-invoices/${invoiceId}/mark-paid`
        : `/hoa/vendor-invoices/${invoiceId}/review`;

      await api.post(endpoint, {
        action,
        reviewer_name: 'HOA Administrator',
        notes: reviewNotes
      });
      toast.success(`Invoice ${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'updated'}`);
      setSelected(null);
      setReviewNotes('');
      fetchInvoices();
      // Tell all financial views to refetch from the server after approval/payment.
      window.dispatchEvent(new CustomEvent('estateflow:financials-updated', {
        detail: { invoiceId, action }
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActing(false);
    }
  };

  const filtered = filter === 'all' ? invoices : invoices.filter(i => i.status === filter);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h3 className="text-lg font-semibold flex items-center gap-2" data-testid="vendor-invoices-title">
          <FileText className="h-5 w-5" /> Vendor Invoices
        </h3>
        <Button variant="outline" size="sm" onClick={fetchInvoices}>Refresh</Button>
      </div>

      {/* Status Counters */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {[
          { key: 'all', label: 'All', count: counts.all },
          { key: 'submitted', label: 'Pending', count: counts.submitted },
          { key: 'under_review', label: 'In Review', count: counts.under_review },
          { key: 'approved', label: 'Approved', count: counts.approved },
          { key: 'paid', label: 'Paid', count: counts.paid },
          { key: 'rejected', label: 'Rejected', count: counts.rejected },
        ].map(s => (
          <button key={s.key} onClick={() => setFilter(s.key)}
            className={`p-2 rounded-lg border text-center text-sm transition-colors ${filter === s.key ? 'bg-slate-800 text-white border-slate-800' : 'bg-white hover:bg-gray-50 border-gray-200'}`}
            data-testid={`filter-${s.key}`}>
            <div className="font-bold text-lg">{s.count}</div>
            <div className="text-xs">{s.label}</div>
          </button>
        ))}
      </div>

      {/* Invoice List */}
      {loading ? (
        <p className="text-center text-gray-500 py-8">Loading invoices...</p>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <FileText className="h-10 w-10 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No invoices found</p>
            <p className="text-sm mt-1">{filter !== 'all' ? 'Try a different filter' : 'Vendor invoices will appear here when submitted'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(inv => {
            const cfg = STATUS_CONFIG[inv.status] || STATUS_CONFIG.submitted;
            const Icon = cfg.icon;
            return (
              <Card key={inv.id} className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => { setSelected(inv); setReviewNotes(''); }}
                data-testid={`invoice-row-${inv.id}`}>
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">#{inv.invoice_number}</span>
                      <Badge className={cfg.color}><Icon className="h-3 w-3 mr-1 inline" />{cfg.label}</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{inv.vendor_name || 'Unknown Vendor'}</p>
                    <p className="text-xs text-gray-500">{inv.description}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Submitted: {inv.submitted_at ? new Date(inv.submitted_at).toLocaleDateString() : 'N/A'}
                      {inv.due_date && ` | Due: ${inv.due_date}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold">${(inv.total || inv.subtotal || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                    {inv.attachments?.length > 0 && (
                      <p className="text-xs text-blue-600 flex items-center justify-end gap-1"><Download className="h-3 w-3" /> {inv.attachments.length} file(s)</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Invoice Detail / Review Modal */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected && (() => {
            const cfg = STATUS_CONFIG[selected.status] || STATUS_CONFIG.submitted;
            const Icon = cfg.icon;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Invoice #{selected.invoice_number}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-lg">{selected.vendor_name}</p>
                      <p className="text-sm text-gray-500">{selected.vendor_email}</p>
                    </div>
                    <Badge className={`${cfg.color} text-sm`}><Icon className="h-3.5 w-3.5 mr-1 inline" />{cfg.label}</Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm bg-gray-50 rounded-lg p-4">
                    <div><span className="text-gray-500">Description:</span><br/><span className="font-medium">{selected.description || '—'}</span></div>
                    <div><span className="text-gray-500">Category:</span><br/><span className="font-medium capitalize">{selected.category || '—'}</span></div>
                    <div><span className="text-gray-500">Submitted:</span><br/><span>{selected.submitted_at ? new Date(selected.submitted_at).toLocaleString() : '—'}</span></div>
                    <div><span className="text-gray-500">Due Date:</span><br/><span>{selected.due_date || '—'}</span></div>
                  </div>

                  {/* Line Items */}
                  {selected.line_items?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-sm mb-2">Line Items</h4>
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr><th className="p-2 text-left">Description</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Rate</th><th className="p-2 text-right">Amount</th></tr>
                          </thead>
                          <tbody>
                            {selected.line_items.map((item, i) => (
                              <tr key={i} className="border-t">
                                <td className="p-2">{item.description}</td>
                                <td className="p-2 text-right">{item.quantity || 1}</td>
                                <td className="p-2 text-right">${(item.rate || item.unit_price || 0).toFixed(2)}</td>
                                <td className="p-2 text-right">${(item.amount || (item.quantity || 1) * (item.rate || item.unit_price || 0)).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Totals */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex justify-between text-sm"><span>Subtotal</span><span>${(selected.subtotal || selected.total || 0).toFixed(2)}</span></div>
                    {selected.tax > 0 && <div className="flex justify-between text-sm mt-1"><span>Tax</span><span>${selected.tax.toFixed(2)}</span></div>}
                    <div className="flex justify-between font-bold text-lg mt-2 pt-2 border-t"><span>Total</span><span>${(selected.total || 0).toFixed(2)}</span></div>
                  </div>

                  {/* Attachments */}
                  {selected.attachments?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-sm mb-2">Attachments</h4>
                      <div className="space-y-1">
                        {selected.attachments.map((att, i) => (
                          <a key={i} href={att.url || att.storage_path || '#'} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-2 text-sm text-blue-600 hover:underline p-2 bg-blue-50 rounded">
                            <Download className="h-4 w-4" /> {att.filename || att.name || `Attachment ${i+1}`}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Approval Chain */}
                  {selected.approval_chain?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-sm mb-2">Approval Workflow</h4>
                      <div className="flex gap-2">
                        {selected.approval_chain.map((step, i) => (
                          <div key={i} className={`flex-1 p-3 rounded-lg border text-center text-sm ${
                            step.status === 'approved' ? 'bg-green-50 border-green-200' :
                            step.status === 'rejected' ? 'bg-red-50 border-red-200' :
                            'bg-gray-50 border-gray-200'
                          }`}>
                            <p className="font-medium capitalize">{step.role === 'property_manager' ? 'PM Review' : 'Board Approval'}</p>
                            <Badge variant="outline" className={`mt-1 text-xs ${
                              step.status === 'approved' ? 'text-green-700' :
                              step.status === 'rejected' ? 'text-red-700' : 'text-gray-500'
                            }`}>{step.status}</Badge>
                            {step.name && <p className="text-xs text-gray-500 mt-1">{step.name}</p>}
                            {step.timestamp && <p className="text-xs text-gray-400">{new Date(step.timestamp).toLocaleDateString()}</p>}
                            {step.notes && <p className="text-xs text-gray-500 mt-1 italic">{step.notes}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selected.notes && (
                    <div><h4 className="font-medium text-sm mb-1">Vendor Notes</h4><p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{selected.notes}</p></div>
                  )}

                  {/* Review Actions */}
                  {(selected.status === 'submitted' || selected.status === 'under_review') && (
                    <div className="border-t pt-4">
                      <h4 className="font-medium text-sm mb-2">Review Actions</h4>
                      <textarea className="w-full px-3 py-2 border rounded-md text-sm resize-none mb-3" rows={2}
                        placeholder="Review notes (optional)..." value={reviewNotes}
                        onChange={e => setReviewNotes(e.target.value)} data-testid="review-notes" />
                      <div className="flex gap-2 flex-wrap">
                        {selected.status === 'submitted' && (
                          <>
                            <Button className="bg-green-600 hover:bg-green-700" disabled={acting}
                              onClick={() => handleReview(selected.id, 'approve', 'review')} data-testid="approve-btn">
                              <CheckCircle className="h-4 w-4 mr-2" /> Approve (PM Review)
                            </Button>
                            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" disabled={acting}
                              onClick={() => handleReview(selected.id, 'reject', 'review')} data-testid="reject-btn">
                              <XCircle className="h-4 w-4 mr-2" /> Reject
                            </Button>
                          </>
                        )}
                        {selected.status === 'under_review' && (
                          <>
                            <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={acting}
                              onClick={() => handleReview(selected.id, 'approve', 'board')} data-testid="board-approve-btn">
                              <CheckCircle className="h-4 w-4 mr-2" /> Board Approve
                            </Button>
                            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" disabled={acting}
                              onClick={() => handleReview(selected.id, 'reject', 'board')} data-testid="board-reject-btn">
                              <XCircle className="h-4 w-4 mr-2" /> Board Reject
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {selected.status === 'approved' && (
                    <div className="border-t pt-4">
                      <Button className="bg-emerald-700 hover:bg-emerald-800" disabled={acting}
                        onClick={() => handleReview(selected.id, 'mark_paid', 'paid')} data-testid="mark-paid-btn">
                        <DollarSign className="h-4 w-4 mr-2" /> Mark as Paid
                      </Button>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorInvoiceReview;
